import { styled } from '@mui/material/styles';
import { useState, useEffect, useMemo, ReactNode, Ref } from 'react';
import { debounce } from 'lodash';
import SearchTextField from '../SearchTextField/SearchTextField';
import SortControl from '../SortControl/SortControl';

const ResourcesPageHeader = ({
  title,
  searchText,
  sortBy,
  onSearchTextChange,
  onSortByChange,
  options,
  sortOrder,
  onSortOrderChange,
  searchPlaceholder,
  propositions,
  loading,
  onSearchAccept,
  onSearchCancel,
  searchPropositionsPopperRef,
  titleAction,
  actions,
  defaultSortBy,
  defaultSortOrder = 'desc',
}: {
  title: ReactNode;
  titleAction?: ReactNode;
  // Rendered on the search/sort row, to the right of the sort control.
  actions?: ReactNode;
  searchText: string;
  sortBy: string;
  onSearchTextChange: (s: string) => void;
  onSortByChange: (s: string) => void;
  options: {
    id: number;
    label: ReactNode;
    value: string;
  }[];
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (v: 'asc' | 'desc') => void;
  searchPlaceholder: string;
  propositions?: ReactNode;
  loading?: boolean;
  onSearchAccept?: () => void;
  onSearchCancel?: () => void;
  searchPropositionsPopperRef?: Ref<HTMLDivElement>;
  defaultSortBy?: string;
  defaultSortOrder?: 'asc' | 'desc';
}) => {
  const [internalSearchText, setInternalSearchText] =
    useState(searchText);

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        onSearchTextChange(value);
      }, 500),
    [onSearchTextChange],
  );

  useEffect(() => {
    setInternalSearchText(searchText);
  }, [searchText]);

  const handleSearchTextChange = (value: string) => {
    setInternalSearchText(value);
    debouncedSearchChange(value);
  };

  const handleSearchClear = () => {
    setInternalSearchText('');
    onSearchTextChange('');
  };

  return (
    <>
      <Title>
        <div>{title}</div>
        {titleAction}
      </Title>
      <Actions>
        <StyledSearchTextField
          placeholder={searchPlaceholder}
          value={internalSearchText}
          onChange={handleSearchTextChange}
          loading={loading}
          onEnterUp={onSearchAccept}
          popperRef={searchPropositionsPopperRef}
          minQueryLength={2}
          onCancel={() => {
            handleSearchClear();
            onSearchCancel?.();
          }}
          propositions={
            internalSearchText && propositions ? (
              <SuggestionsContainer>
                {propositions}
              </SuggestionsContainer>
            ) : null
          }
          aria-label="Search resources"
        />
        <ActionsRight>
          <SortControl
            sortBy={sortBy}
            onSortByChange={onSortByChange}
            options={options}
            sortOrder={sortOrder}
            onSortOrderChange={onSortOrderChange}
            defaultSortBy={defaultSortBy}
            defaultSortOrder={defaultSortOrder}
          />
          {actions}
        </ActionsRight>
      </Actions>
    </>
  );
};

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  color: theme.palette.black,
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  marginTop: 40,
  marginBottom: 8,
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const ActionsRight = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StyledSearchTextField = styled(SearchTextField)({
  maxWidth: 500,
});

const SuggestionsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export default ResourcesPageHeader;
